import React, { useEffect, useState, useRef } from 'react';
import { Mic, Square, Sparkles, Edit2, PenLine, Upload, RefreshCw, ArrowRightIcon, Plus, X, Check, ArrowLeftIcon, ChevronLeft, Trash2 } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';
// === MOCK AI TIMINGS ===
// In produzione sostituire con chiamate reali a /api/project/generate.
// Vedere docs/EIIS-Summit-PWA-Technical-Handoff.md §5.3.
const MOCK_AI_DELAY_ANALYSIS_MS = 1800;
const MOCK_AI_DELAY_GENERATION_MS = 2200;
interface Props {
  onContinue: () => void;
  onSkip?: () => void;
  onBack?: () => void;
}
type InputMode = 'upload' | 'voice' | 'text';
type RecordState = 'idle' | 'recording' | 'preview' | 'processing' | 'questions' | 'submitting' | 'done';
const MAX_RECORD_SECONDS = 60;
const MAX_REGENERATIONS = 3;
function tryVibrate(ms: number) {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(ms);
    }
  } catch {
    // fail silently
  }
}
// TODO: estrarre la project card review come step 4/5 dedicato quando si introdurrà editing inline per title/topics/description.
export function OnboardingProjects({
  onContinue,
  onSkip,
  onBack
}: Props) {
  const {
    t
  } = useLanguage();
  const [inputMode, setInputMode] = useState<InputMode>('upload');
  const [recordState, setRecordState] = useState<RecordState>('idle');
  const [timer, setTimer] = useState(0);
  const [textInput, setTextInput] = useState('');
  const [mounted, setMounted] = useState(false);
  const [regenCount, setRegenCount] = useState(0);
  const [recordedDuration, setRecordedDuration] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [projectTitle, setProjectTitle] = useState('Piattaforma di tracciabilità per la filiera del cacao');
  const [projectDesc, setProjectDesc] = useState("Stiamo sviluppando un sistema basato su blockchain per garantire un compenso equo ai coltivatori in Costa d'Avorio, riducendo gli intermediari.");
  const [projectTags, setProjectTags] = useState<string[]>(['Innovative Finance', 'New Partnership Models', 'Frontier Technologies']);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  // Prompt 13: follow-up question answers (optional, mock demo)
  const [followupQ1, setFollowupQ1] = useState<string | null>(null);
  const [followupQ2, setFollowupQ2] = useState<string | null>(null);
  const [followupQ1Custom, setFollowupQ1Custom] = useState('');
  const [followupQ2Custom, setFollowupQ2Custom] = useState('');
  // NEW: current question index for multi-page questions
  const [currentQuestion, setCurrentQuestion] = useState(0);
  // Delete confirmation: two-step (arm then confirm). Auto-disarm after 3s.
  const [deleteArmed, setDeleteArmed] = useState(false);
  const deleteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const tm = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(tm);
  }, []);
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    if (recordState === 'recording') {
      interval = setInterval(() => {
        setTimer(prev => {
          const next = prev + 1;
          if (next >= MAX_RECORD_SECONDS) {
            // Auto-stop at max duration → go to preview
            tryVibrate(20);
            setRecordedDuration(next);
            setRecordState('preview');
            return next;
          }
          return next;
        });
      }, 1000);
    } else if (recordState === 'idle') {
      setTimer(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [recordState]);
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const handleStartRecording = () => {
    if (recordState !== 'idle') return;
    tryVibrate(20);
    setTimer(0);
    setRecordedDuration(0);
    setRecordState('recording');
  };
  const handleStopRecording = () => {
    if (recordState !== 'recording') return;
    tryVibrate(20);
    setRecordedDuration(timer);
    setRecordState('preview');
  };
  const handleRecordAgain = () => {
    setRecordedDuration(0);
    setTimer(0);
    setRecordState('idle');
  };
  const handleSubmitRecording = () => {
    setRecordState('processing');
    // MOCK: simula tempo di elaborazione AI. In prod: await api.analyze(recording).
    setTimeout(() => {
      setCurrentQuestion(0);
      setRecordState('questions');
    }, MOCK_AI_DELAY_ANALYSIS_MS);
  };
  const handleTextSubmit = () => {
    if (textInput.trim().length < 10) return;
    setRecordState('processing');
    // MOCK: simula tempo di elaborazione AI. In prod: await api.analyze(text).
    setTimeout(() => {
      setCurrentQuestion(0);
      setRecordState('questions');
    }, MOCK_AI_DELAY_ANALYSIS_MS);
  };
  const handleUploadClick = () => {
    if (recordState !== 'idle') return;
    fileInputRef.current?.click();
  };
  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFileName(file.name);
    setRecordState('processing');
    // MOCK: simula tempo di elaborazione AI. In prod: await api.analyze(file).
    setTimeout(() => {
      setCurrentQuestion(0);
      setRecordState('questions');
    }, MOCK_AI_DELAY_ANALYSIS_MS);
    e.target.value = '';
  };
  const openEdit = () => {
    setEditTitle(projectTitle);
    setEditDesc(projectDesc);
    setEditTags([...projectTags]);
    setNewTag('');
    setIsEditing(true);
  };
  const saveEdit = () => {
    setProjectTitle(editTitle.trim() || projectTitle);
    setProjectDesc(editDesc.trim() || projectDesc);
    setProjectTags(editTags);
    setIsEditing(false);
  };
  const cancelEdit = () => {
    setIsEditing(false);
  };
  const addTag = () => {
    const v = newTag.trim();
    if (!v) return;
    if (editTags.includes(v)) return;
    setEditTags([...editTags, v]);
    setNewTag('');
  };
  const removeTag = (tag: string) => {
    setEditTags(editTags.filter(x => x !== tag));
  };
  const handleModeSwitch = (mode: InputMode) => {
    if (recordState === 'recording') return;
    if (mode === inputMode) return;
    setInputMode(mode);
    setRecordState('idle');
    setTextInput('');
    setUploadedFileName(null);
    setFollowupQ1(null);
    setFollowupQ2(null);
    setFollowupQ1Custom('');
    setFollowupQ2Custom('');
    setCurrentQuestion(0);
  };
  const handleRegenerate = () => {
    if (regenCount >= MAX_REGENERATIONS) return;
    setRegenCount(c => c + 1);
    setRecordState('submitting');
    // MOCK: simula tempo di elaborazione AI. In prod: await api.regenerate(project).
    setTimeout(() => setRecordState('done'), MOCK_AI_DELAY_ANALYSIS_MS);
  };
  // Questions definition for multi-page flow
  const questions = [{
    title: t('projects.followup.q1'),
    options: [{
      key: 'a1',
      label: t('projects.followup.q1.a1')
    }, {
      key: 'a2',
      label: t('projects.followup.q1.a2')
    }, {
      key: 'a3',
      label: t('projects.followup.q1.a3')
    }, {
      key: 'a4',
      label: t('projects.followup.q1.a4')
    }],
    selected: followupQ1,
    setSelected: setFollowupQ1,
    customValue: followupQ1Custom,
    setCustomValue: setFollowupQ1Custom
  }, {
    title: t('projects.followup.q2'),
    options: [{
      key: 'a1',
      label: t('projects.followup.q2.a1')
    }, {
      key: 'a2',
      label: t('projects.followup.q2.a2')
    }, {
      key: 'a3',
      label: t('projects.followup.q2.a3')
    }, {
      key: 'a4',
      label: t('projects.followup.q2.a4')
    }],
    selected: followupQ2,
    setSelected: setFollowupQ2,
    customValue: followupQ2Custom,
    setCustomValue: setFollowupQ2Custom
  }];
  const isLastQuestion = currentQuestion === questions.length - 1;
  const currentQ = questions[currentQuestion];
  const canSubmit = followupQ1 !== null || followupQ1Custom.trim().length > 0;
  const q2Answered = followupQ2 !== null || followupQ2Custom.trim().length > 0;
  const allAnswered = (followupQ1 !== null || followupQ1Custom.trim().length > 0) && q2Answered;
  const handleQuestionSelect = (key: string) => {
    currentQ.setSelected(key);
    currentQ.setCustomValue('');
    // Auto-advance to next question (if not last)
    if (!isLastQuestion) {
      setTimeout(() => setCurrentQuestion(c => c + 1), 250);
    }
  };
  const handleQuestionNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(c => c + 1);
    }
  };
  const handleQuestionBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(c => c - 1);
    }
  };
  const handleSubmitAnswers = () => {
    setRecordState('submitting');
    // MOCK: simula tempo di generazione AI della project card. In prod: await api.generate(answers).
    setTimeout(() => setRecordState('done'), MOCK_AI_DELAY_GENERATION_MS);
  };
  const handleAddAnother = () => {
    setRecordState('idle');
    setTextInput('');
    setUploadedFileName(null);
    setRegenCount(0);
    setFollowupQ1(null);
    setFollowupQ2(null);
    setFollowupQ1Custom('');
    setFollowupQ2Custom('');
    setCurrentQuestion(0);
  };
  const handleDeleteClick = () => {
    if (!deleteArmed) {
      setDeleteArmed(true);
      tryVibrate(15);
      if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
      deleteTimerRef.current = setTimeout(() => setDeleteArmed(false), 3000);
      return;
    }
    if (deleteTimerRef.current) {
      clearTimeout(deleteTimerRef.current);
      deleteTimerRef.current = null;
    }
    setDeleteArmed(false);
    tryVibrate(30);
    handleAddAnother();
  };
  useEffect(() => {
    return () => {
      if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
    };
  }, []);
  const isIdle = recordState === 'idle';
  const regenDisabled = regenCount >= MAX_REGENERATIONS;
  return <div className="flex flex-col h-full w-full bg-[#0D0D0D] relative" data-id="element-502">
      {onBack && <button onClick={onBack} aria-label="Back" className="absolute top-6 left-6 z-50 w-10 h-10 rounded-full bg-[#1A1A1A]/80 backdrop-blur-md border border-[#2A2A2A] text-[#888888] flex items-center justify-center hover:text-white transition-colors" data-id="element-503">
          <ChevronLeft className="w-6 h-6 -ml-0.5" strokeWidth={2} data-id="element-504" />
        </button>}
      <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.txt,image/*" onChange={handleFileSelected} className="hidden" data-id="element-505" />

      <div className="flex-1 overflow-y-auto px-6 pt-20 pb-36" data-id="element-506">
        {/* Progress Bar */}
        <div className="w-full transition-all duration-[800ms] ease-in-out" style={{
        opacity: mounted ? 1 : 0,
        transitionDelay: '0ms'
      }} data-id="element-507">
          <div className="w-full h-1 bg-[#1A1A1A] rounded-full overflow-hidden" data-id="element-508">
            <div className="h-full bg-[#C5D82D] w-[75%] rounded-full transition-all duration-500" data-id="element-509" />
          </div>
          <div className="flex items-center justify-end mt-1.5" data-id="element-510">
            <span className="text-[#888888] text-[12px]" data-id="element-511">
              {t('project.progress')}
            </span>
          </div>
        </div>

        {/* Header */}
        <div className="mt-6 mb-6 transition-all duration-[800ms] ease-in-out" style={{
        opacity: mounted ? 1 : 0,
        transitionDelay: '120ms'
      }} data-id="element-512">
          <h1 className="text-[20px] text-white font-semibold" data-id="element-513">
            {'Raccontaci il tuo progetto'}
          </h1>
        </div>

        {/* Main Content Area */}
        <div className="flex flex-col items-center transition-all duration-[800ms] ease-in-out" style={{
        opacity: mounted ? 1 : 0,
        transitionDelay: '250ms'
      }} data-id="element-514">
          {/* === IDLE STATE === */}
          {isIdle && <div className="w-full flex flex-col items-center" data-id="element-515">
              {/* Segmented Mode Toggle — Upload | Voice | Text */}
              <div className="bg-[#141414] border border-[#2A2A2A] rounded-xl p-1 flex mb-3 w-full" data-id="element-516">
                <button onClick={() => handleModeSwitch('upload')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] transition-all duration-200 font-medium ${inputMode === 'upload' ? 'bg-[#2A2A2A] text-white' : 'text-[#999999] hover:text-[#CCCCCC]'}`} data-id="element-517">
                  <Upload className="w-4 h-4" strokeWidth={2} data-id="element-518" />
                  {t('projects.tab.upload')}
                </button>
                <button onClick={() => handleModeSwitch('voice')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] transition-all duration-200 font-medium ${inputMode === 'voice' ? 'bg-[#2A2A2A] text-white' : 'text-[#999999] hover:text-[#CCCCCC]'}`} data-id="element-519">
                  <Mic className="w-4 h-4" strokeWidth={2} data-id="element-520" />
                  {t('projects.tab.voice')}
                </button>
                <button onClick={() => handleModeSwitch('text')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] transition-all duration-200 font-medium ${inputMode === 'text' ? 'bg-[#2A2A2A] text-white' : 'text-[#999999] hover:text-[#CCCCCC]'}`} data-id="element-521">
                  <PenLine className="w-4 h-4" strokeWidth={2} data-id="element-522" />
                  {t('projects.tab.write')}
                </button>
              </div>
              <p className="text-[13px] text-[#A0A0A0] text-center mb-6 leading-relaxed" data-id="element-523">
                {inputMode === 'upload' ? "Carica un documento, una presentazione o un'immagine del tuo progetto." : inputMode === 'voice' ? 'Racconta il tuo progetto a voce: cosa fai e perché è importante.' : 'Descrivi brevemente il tuo progetto: obiettivi, impatto e fase attuale.'}
              </p>

              {/* Upload Mode */}
              {inputMode === 'upload' && <button type="button" onClick={handleUploadClick} className="w-full min-h-[180px] rounded-xl border-2 border-dashed border-[#2A2A2A] bg-[#141414] flex flex-col items-center justify-center cursor-pointer hover:border-[#C5D82D]/40 transition-colors px-6 py-8" data-id="element-524">
                  <Upload className="w-10 h-10 text-[#C5D82D]" strokeWidth={1.5} data-id="element-525" />
                  <p className="text-[15px] text-white font-medium mt-3 text-center" data-id="element-526">
                    {t('project.uploadPrompt')}
                  </p>
                  <p className="text-[13px] mt-1 text-center text-[#C4D82D]" data-id="element-527">
                    {t('projects.upload.hint')}
                  </p>
                </button>}

              {/* Voice Mode */}
              {inputMode === 'voice' && <div className="flex flex-col items-center" data-id="element-528">
                  <button type="button" onClick={handleStartRecording} className="w-24 h-24 bg-[#141414] border border-[#2A2A2A] rounded-full flex items-center justify-center text-[#C5D82D] hover:border-[#C5D82D]/50 transition-all duration-300 active:scale-95 group relative select-none" data-id="element-529">
                    <div className="absolute inset-0 bg-[#C5D82D] rounded-full opacity-0 group-hover:opacity-5 blur-2xl transition-opacity duration-300" data-id="element-530" />
                    <Mic className="w-10 h-10 relative z-10" strokeWidth={1.5} data-id="element-531" />
                  </button>
                  <p className="mt-5 text-[15px] text-white font-medium" data-id="element-532">
                    {t('project.tapToRecord')}
                  </p>
                  <p className="mt-2 text-[13px] text-[#999999]" data-id="element-533">
                    {t('project.maxSeconds')}
                  </p>
                </div>}

              {/* Text Mode */}
              {inputMode === 'text' && <div className="w-full space-y-3" data-id="element-534">
                  <div className="relative" data-id="element-535">
                    <textarea value={textInput} onChange={e => setTextInput(e.target.value)} placeholder={t('project.placeholder')} className="w-full bg-[#141414] p-4 pb-9 rounded-xl border border-[#2A2A2A] text-white placeholder:text-[#444444] focus:outline-none focus:border-[#2A2A2A] transition-colors text-[15px] resize-none h-[150px]" data-id="element-536" />
                    <p className="absolute bottom-3 right-4 text-[12px] text-[#666666] tabular-nums pointer-events-none" data-id="element-537">
                      {textInput.length > 0 ? `${textInput.length} ${t('project.charCount')}` : t('project.minChars')}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2" data-id="element-538">
                    <button onClick={handleTextSubmit} disabled={textInput.trim().length < 10} className={`flex items-center justify-center gap-2 py-3 rounded-full text-[13px] font-medium transition-all duration-200 border ${textInput.trim().length >= 10 ? 'bg-transparent border-[#2A2A2A] text-[#C5D82D] hover:bg-[#C5D82D]/10 hover:border-[#C5D82D]/40' : 'bg-transparent border-[#1A1A1A] text-[#555555] opacity-50 cursor-not-allowed'}`} data-id="element-539">
                      <Sparkles className="w-4 h-4 shrink-0" strokeWidth={2} data-id="element-540" />
                      <span className="truncate" data-id="element-541">
                        {t('projects.rewriteAI')}
                      </span>
                    </button>
                    <button onClick={handleTextSubmit} disabled={textInput.trim().length < 10} className={`flex items-center justify-center gap-2 py-3 rounded-full text-[13px] font-semibold transition-all duration-200 ${textInput.trim().length >= 10 ? 'bg-[#C5D82D] text-[#0D0D0D] hover:bg-[#d4e63a]' : 'bg-[#1A1A1A] text-[#555555] opacity-50 cursor-not-allowed'}`} data-id="element-542">
                      <Check className="w-4 h-4 shrink-0" strokeWidth={2.5} data-id="element-543" />
                      <span className="truncate" data-id="element-544">{t('projects.save')}</span>
                    </button>
                  </div>
                </div>}
            </div>}

          {/* === RECORDING STATE === */}
          {recordState === 'recording' && <div className="flex flex-col items-center pt-4" data-id="element-545">
              <div className="relative" data-id="element-546">
                <div className="absolute inset-0 border border-[#D32F2F] rounded-full animate-ping opacity-20" data-id="element-547" />
                <div className="absolute -inset-5 border border-[#D32F2F] rounded-full animate-pulse opacity-10" data-id="element-548" />
                <button type="button" onClick={handleStopRecording} className="w-28 h-28 bg-[#141414] border border-[#D32F2F] rounded-full flex items-center justify-center text-[#D32F2F] active:scale-95 transition-all relative z-10" data-id="element-549">
                  <Square className="w-10 h-10 fill-current" data-id="element-550" />
                </button>
              </div>
              <p className="mt-6 text-[15px] text-[#D32F2F] tracking-wider font-medium" data-id="element-551">
                {formatTime(timer)}
              </p>
              <p className="mt-4 text-[13px] text-[#888888]" data-id="element-552">
                {t('project.tapToStop')}
              </p>
            </div>}

          {/* === PREVIEW STATE (review recording before sending to AI) === */}
          {recordState === 'preview' && <div className="w-full flex flex-col items-center pt-2" data-id="element-553">
              <div className="w-full bg-[#141414] border border-[#2A2A2A] rounded-2xl p-5" data-id="element-554">
                <div className="flex items-center gap-3" data-id="element-555">
                  <button type="button" className="w-12 h-12 shrink-0 rounded-full bg-[#C5D82D] text-[#0D0D0D] flex items-center justify-center active:scale-95 transition-transform" aria-label={t('projects.playRecording')} data-id="element-556">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 ml-0.5" data-id="element-557">
                      <path d="M8 5v14l11-7z" data-id="element-558" />
                    </svg>
                  </button>
                  <div className="flex-1 min-w-0" data-id="element-559">
                    <div className="flex items-end gap-[2px] h-8" data-id="element-560">
                      {Array.from({
                    length: 32
                  }).map((_, i) => {
                    const h = 25 + Math.abs(Math.sin(i * 1.3)) * 75;
                    return <div key={i} className="flex-1 bg-[#C5D82D]/60 rounded-sm" style={{
                      height: `${h}%`
                    }} data-id="element-561" />;
                  })}
                    </div>
                    <p className="mt-2 text-[12px] text-[#999999] tabular-nums" data-id="element-562">
                      {formatTime(recordedDuration)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="w-full grid grid-cols-2 gap-3 mt-4" data-id="element-563">
                <button type="button" onClick={handleRecordAgain} className="py-3 rounded-full border border-[#2A2A2A] text-white text-[13px] font-medium flex items-center justify-center gap-2 hover:border-[#4A4A4A] active:scale-[0.98] transition-all" data-id="element-564">
                  <Mic className="w-4 h-4" strokeWidth={2} data-id="element-565" />
                  {t('projects.recordAgain')}
                </button>
                <button type="button" onClick={handleSubmitRecording} className="py-3 rounded-full bg-[#C5D82D] text-[#0D0D0D] text-[13px] font-semibold flex items-center justify-center gap-2 hover:bg-[#d4e63a] active:scale-[0.98] transition-all" data-id="element-566">
                  <Sparkles className="w-4 h-4" strokeWidth={2.5} data-id="element-567" />
                  {t('projects.submitToAI')}
                </button>
              </div>
            </div>}

          {/* === PROCESSING STATE (1st AI loader after input) === */}
          {recordState === 'processing' && <div className="flex flex-col items-center pt-4" data-id="element-568">
              <div className="w-28 h-28 bg-[#141414] border border-[#2A2A2A] rounded-full flex items-center justify-center relative" data-id="element-569">
                <Sparkles className="w-10 h-10 text-[#C5D82D] animate-pulse" data-id="element-570" />
                <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100" data-id="element-571">
                  <circle cx="50" cy="50" r="48" fill="none" stroke="#C5D82D" strokeWidth="2" strokeDasharray="301.59" strokeDashoffset="301.59" className="animate-[dash_2.5s_ease-in-out_forwards]" data-id="element-572" />
                </svg>
              </div>
              <p className="mt-6 text-[14px] text-[#A0A0A0]" data-id="element-573">
                {t('projects.ai.analyzing')}
              </p>
            </div>}

          {/* === QUESTIONS STATE (multi-page, one question at a time) === */}
          {recordState === 'questions' && <div className="w-full" data-id="element-574">
              {/* Question pager indicator */}
              <div className="flex items-center justify-between mb-4" data-id="element-575">
                <button onClick={handleQuestionBack} disabled={currentQuestion === 0} className={`flex items-center gap-1.5 text-[13px] transition-colors ${currentQuestion === 0 ? 'text-[#444444] cursor-not-allowed' : 'text-[#999999] hover:text-white'}`} data-id="element-576">
                  <ArrowLeftIcon className="w-4 h-4" strokeWidth={2} data-id="element-577" />
                  {t('projects.back')}
                </button>
                <div className="flex items-center gap-1.5" data-id="element-578">
                  {questions.map((_, i) => <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === currentQuestion ? 'w-6 bg-[#C5D82D]' : i < currentQuestion ? 'w-1.5 bg-[#C5D82D]/60' : 'w-1.5 bg-[#2A2A2A]'}`} data-id="element-579" />)}
                </div>
                <span className="text-[12px] text-[#888888] font-medium" data-id="element-580">
                  {currentQuestion + 1}/{questions.length}
                </span>
              </div>

              {/* Single question card */}
              <div key={currentQuestion} className="bg-[#141414] border border-[#2A2A2A] rounded-[16px] p-5 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-581">
                <div className="text-[12px] text-[#C5D82D] mb-2 flex items-center gap-1.5 font-medium" data-id="element-582">
                  <Sparkles className="w-3.5 h-3.5" strokeWidth={2} data-id="element-583" />
                  {t('projects.questionLabel', {
                n: currentQuestion + 1
              })}
                </div>
                <h4 className="text-[16px] text-white font-medium mb-5 leading-snug" data-id="element-584">
                  {currentQ.title}
                </h4>
                <div className="flex flex-col gap-2" data-id="element-585">
                  {currentQ.options.map(opt => {
                const isSelected = currentQ.selected === opt.key;
                return <button key={opt.key} type="button" onClick={() => handleQuestionSelect(opt.key)} className={`w-full text-left px-4 py-3 rounded-[12px] text-[14px] transition-all duration-200 border ${isSelected ? 'bg-[#C5D82D]/15 border-[#C5D82D] text-[#C5D82D]' : 'bg-[#1A1A1A] border-[#2A2A2A] text-white hover:border-[#4A4A4A]'}`} data-id="element-586">
                        {opt.label}
                      </button>;
              })}
                </div>
                <div className="mt-4" data-id="element-587">
                  <label className="text-[11px] text-[#888888] uppercase tracking-wide mb-1.5 block" data-id="element-588">
                    {t('projects.orAddOwn')}
                  </label>
                  <input type="text" value={currentQ.customValue} onChange={e => {
                currentQ.setCustomValue(e.target.value);
                if (e.target.value.length > 0) currentQ.setSelected(null);
              }} placeholder={t('projects.followup.custom')} className="w-full bg-[#1A1A1A] border border-[#2A2A2A] rounded-[10px] px-3 py-2.5 text-[14px] text-white placeholder:text-[#6A6A6A] focus:outline-none focus-visible:border-[#C5D82D]" data-id="element-589" />
                  {currentQ.customValue.trim().length > 0 && !isLastQuestion && <button onClick={handleQuestionNext} className="mt-3 ml-auto flex items-center gap-1.5 text-[13px] text-[#C5D82D] font-medium hover:underline" data-id="element-590">
                        {t('projects.next')}
                        <ArrowRightIcon className="w-3.5 h-3.5" strokeWidth={2} data-id="element-591" />
                      </button>}
                </div>
              </div>
            </div>}

          {/* === SUBMITTING STATE (2nd AI loader after submit) === */}
          {recordState === 'submitting' && <div className="flex flex-col items-center pt-4" data-id="element-592">
              <div className="w-28 h-28 bg-[#141414] border border-[#2A2A2A] rounded-full flex items-center justify-center relative" data-id="element-593">
                <Sparkles className="w-10 h-10 text-[#C5D82D] animate-pulse" data-id="element-594" />
                <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100" data-id="element-595">
                  <circle cx="50" cy="50" r="48" fill="none" stroke="#C5D82D" strokeWidth="2" strokeDasharray="301.59" strokeDashoffset="301.59" className="animate-[dash_2.5s_ease-in-out_forwards]" data-id="element-596" />
                </svg>
              </div>
              <p className="mt-6 text-[14px] text-[#A0A0A0]" data-id="element-597">
                {t('projects.ai.analyzing')}
              </p>
              <p className="mt-2 text-[12px] text-[#666666]" data-id="element-598">
                {t('projects.generatingSummary')}
              </p>
            </div>}

          {/* === DONE STATE (summary card) === */}
          {recordState === 'done' && <div className="w-full" data-id="element-599">
              <div className="bg-[#141414] p-5 rounded-xl border border-[#2A2A2A] relative overflow-hidden" data-id="element-600">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5D82D] to-transparent opacity-40" data-id="element-601" />

                <div className="flex items-center justify-between mb-4" data-id="element-602">
                  <div className="flex items-center gap-2" data-id="element-603">
                    <div className="w-6 h-6 bg-[#C5D82D]/15 rounded-full flex items-center justify-center" data-id="element-604">
                      <Sparkles className="w-4 h-4 text-[#C5D82D]" data-id="element-605" />
                    </div>
                    <span className="text-[13px] text-[#C5D82D] font-medium" data-id="element-606">
                      {t('project.generated')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-607">
                    <button onClick={openEdit} className="w-8 h-8 bg-[#1A1A1A] rounded-full flex items-center justify-center text-[#999999] hover:text-white transition-colors border border-[#2A2A2A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5D82D] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D0D0D]" aria-label={t('a11y.editCard')} data-id="element-608">
                      <Edit2 className="w-4 h-4" data-id="element-609" />
                    </button>
                    <button onClick={handleDeleteClick} onBlur={() => {
                  if (deleteTimerRef.current) {
                    clearTimeout(deleteTimerRef.current);
                    deleteTimerRef.current = null;
                  }
                  setDeleteArmed(false);
                }} className={`h-8 rounded-full flex items-center justify-center transition-all duration-200 border focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D0D0D] ${deleteArmed ? 'px-3 gap-1.5 bg-[#D32F2F]/15 border-[#D32F2F] text-[#FF6B6B] focus-visible:ring-[#D32F2F] animate-in fade-in zoom-in-95' : 'w-8 bg-[#1A1A1A] border-[#2A2A2A] text-[#999999] hover:text-[#FF6B6B] hover:border-[#D32F2F]/40 focus-visible:ring-[#C5D82D]'}`} aria-label={deleteArmed ? t('projects.confirmDeleteAria') : t('projects.deleteProject')} data-id="element-610">
                      <Trash2 className="w-4 h-4" strokeWidth={2} data-id="element-611" />
                      {deleteArmed && <span className="text-[12px] font-semibold" data-id="element-612">
                          {t('projects.confirmDelete')}
                        </span>}
                    </button>
                  </div>
                </div>

                <h3 className="text-[15px] text-white mb-3 font-medium" data-id="element-613">
                  {projectTitle}
                </h3>
                <p className="text-[15px] text-[#888888] mb-4" data-id="element-614">{projectDesc}</p>

                <div className="flex flex-wrap gap-1.5" data-id="element-615">
                  {projectTags.map(tag => <span key={tag} className="px-2.5 py-1 bg-[#1A1A1A] rounded-md border border-[#2A2A2A] text-[#888888] text-[13px]" data-id="element-616">
                      {tag}
                    </span>)}
                </div>
              </div>

              {/* Add another project */}
              <button onClick={handleAddAnother} className="mt-5 w-full flex items-center justify-center gap-2 py-3 rounded-full border border-dashed border-[#2A2A2A] text-[13px] text-[#999999] hover:text-[#CCCCCC] hover:border-[#4A4A4A] transition-colors font-medium" data-id="element-617">
                <Plus className="w-4 h-4" strokeWidth={2} data-id="element-618" />
                {t('project.addAnother')}
              </button>
            </div>}
        </div>
      </div>

      {/* Fixed Bottom Bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#0D0D0D]/95 backdrop-blur-xl border-t border-[#1A1A1A] px-6 pt-4 pb-8 transition-all duration-[800ms] ease-in-out z-40" style={{
      opacity: mounted ? 1 : 0,
      transitionDelay: '400ms'
    }} data-id="element-619">
        <div className="flex flex-col" data-id="element-620">
          {(isIdle || recordState === 'recording') && <button onClick={onSkip} className="w-full mb-2 py-3 text-[13px] text-[#888888] hover:text-[#AAAAAA] transition-colors text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5D82D] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D0D0D] rounded-lg" data-id="element-621">
              {t('project.skip')}
            </button>}

          {/* Submit button shown only on last question when answered */}
          {recordState === 'questions' && isLastQuestion && q2Answered && <button onClick={handleSubmitAnswers} className="w-full py-4 rounded-full bg-[#C5D82D] text-[#0D0D0D] text-[14px] font-semibold tracking-wide flex items-center justify-center gap-2 transition-all hover:bg-[#d4e63a] active:scale-[0.98]" data-id="element-622">
              <Check className="w-4 h-4" strokeWidth={2.5} data-id="element-623" />
              {t('projects.submit')}
            </button>}

          {/* Continue button shown on done state or as passive state on idle/processing/submitting */}
          {recordState !== 'questions' && recordState !== 'preview' && <button onClick={onContinue} disabled={recordState === 'processing' || recordState === 'submitting'} className={`w-full py-4 rounded-full border-[1.5px] border-[#C5D82D] text-[#C5D82D] bg-transparent text-[14px] font-semibold tracking-wide flex items-center justify-center gap-2 transition-colors hover:bg-[#C5D82D]/10 ${recordState === 'processing' || recordState === 'submitting' ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-624">
              {recordState === 'processing' || recordState === 'submitting' ? t('projects.ai.analyzing') : t('project.cta')}
              {recordState !== 'processing' && recordState !== 'submitting' && <ArrowRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-625" />}
            </button>}

          {/* Placeholder button when on questions state but not ready to submit — show disabled continue */}
          {recordState === 'questions' && !(isLastQuestion && q2Answered) && <button disabled className="w-full py-4 rounded-full border-[1.5px] border-[#2A2A2A] text-[#555555] bg-transparent text-[14px] font-semibold tracking-wide flex items-center justify-center gap-2 cursor-not-allowed" data-id="element-626">
              {isLastQuestion ? t('projects.answerToContinue') : questions.length - currentQuestion > 1 ? t('projects.answerMoreMany', {
            n: questions.length - currentQuestion
          }) : t('projects.answerMoreOne', {
            n: questions.length - currentQuestion
          })}
            </button>}
        </div>
      </div>

      {/* Edit modal for summary card */}
      {isEditing && <div className="absolute inset-0 z-50 flex flex-col justify-end" data-id="element-627">
          <div className="absolute inset-0 bg-black/70 animate-in fade-in duration-200" onClick={cancelEdit} data-id="element-628" />
          <div className="relative bg-[#141414] rounded-t-2xl border-t border-[#2A2A2A] pt-6 pb-8 px-6 max-h-[85%] overflow-y-auto" style={{
        animation: 'drawerSlideUp 280ms cubic-bezier(0.16, 1, 0.3, 1) both'
      }} data-id="element-629">
            <div className="flex items-center justify-between mb-5" data-id="element-630">
              <h2 className="text-[15px] text-white font-medium" data-id="element-631">
                {t('projects.editProject')}
              </h2>
              <button onClick={cancelEdit} className="w-8 h-8 bg-[#1A1A1A] rounded-full flex items-center justify-center text-[#888888] border border-[#2A2A2A]" aria-label={t('projects.close')} data-id="element-632">
                <X className="w-5 h-5" data-id="element-633" />
              </button>
            </div>

            <div className="space-y-4" data-id="element-634">
              <div data-id="element-635">
                <label className="text-[12px] text-[#888888] mb-1.5 block" data-id="element-636">
                  {t('projects.title')}
                </label>
                <input type="text" value={editTitle} onChange={e => setEditTitle(e.target.value)} className="w-full bg-[#1A1A1A] border border-[#2A2A2A] rounded-[10px] px-3 py-2.5 text-[14px] text-white focus:outline-none focus-visible:border-[#C5D82D]" data-id="element-637" />
              </div>
              <div data-id="element-638">
                <label className="text-[12px] text-[#888888] mb-1.5 block" data-id="element-639">
                  {t('projects.description')}
                </label>
                <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={4} className="w-full bg-[#1A1A1A] border border-[#2A2A2A] rounded-[10px] px-3 py-2.5 text-[14px] text-white focus:outline-none focus-visible:border-[#C5D82D] resize-none" data-id="element-640" />
              </div>
              <div data-id="element-641">
                <label className="text-[12px] text-[#888888] mb-1.5 block" data-id="element-642">
                  {t('projects.topics')}
                </label>
                <div className="flex flex-wrap gap-1.5 mb-2" data-id="element-643">
                  {editTags.map(tag => <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#1A1A1A] rounded-md border border-[#2A2A2A] text-[#CCCCCC] text-[13px]" data-id="element-644">
                      {tag}
                      <button onClick={() => removeTag(tag)} className="text-[#888888] hover:text-white" data-id="element-645">
                        <X className="w-3 h-3" data-id="element-646" />
                      </button>
                    </span>)}
                </div>
                <div className="flex gap-2" data-id="element-647">
                  <input type="text" value={newTag} onChange={e => setNewTag(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag();
                }
              }} placeholder={t('projects.addTopic')} className="flex-1 bg-[#1A1A1A] border border-[#2A2A2A] rounded-[10px] px-3 py-2 text-[14px] text-white placeholder:text-[#6A6A6A] focus:outline-none focus-visible:border-[#C5D82D]" data-id="element-648" />
                  <button onClick={addTag} className="px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-[10px] text-[#C5D82D] text-[13px] font-medium" data-id="element-649">
                    {t('projects.add')}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6" data-id="element-650">
              <button onClick={cancelEdit} className="flex-1 py-3 rounded-full border border-[#2A2A2A] text-white text-[14px] font-medium" data-id="element-651">
                {t('projects.cancel')}
              </button>
              <button onClick={saveEdit} className="flex-1 py-3 rounded-full bg-[#C5D82D] text-[#0D0D0D] text-[14px] font-semibold" data-id="element-652">
                {t('projects.save')}
              </button>
            </div>
          </div>
        </div>}

      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes dash {
          to { stroke-dashoffset: 0; }
        }
        @keyframes drawerSlideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: none; opacity: 1; }
        }
      `
    }} data-id="element-653" />
    </div>;
}